import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

interface ContextChip {
  label: string;
  value: string;
}

interface CopilotContextValue {
  contextChips: ContextChip[];
  setContextChips: (chips: ContextChip[]) => void;
  addContextChip: (chip: ContextChip) => void;
  removeContextChip: (value: string) => void;
  clearContextChips: () => void;
}

const CopilotContext = createContext<CopilotContextValue | undefined>(undefined);

export function CopilotProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [contextChips, setContextChips] = useState<ContextChip[]>([]);

  const addContextChip = (chip: ContextChip) => {
    setContextChips((prev) => {
      // Avoid duplicates
      if (prev.some((c) => c.value === chip.value)) {
        return prev;
      }
      return [...prev, chip];
    });
  };

  const removeContextChip = (value: string) => {
    setContextChips((prev) => prev.filter((chip) => chip.value !== value));
  };

  const clearContextChips = () => {
    setContextChips([]);
  };

  return (
    <CopilotContext.Provider
      value={{
        contextChips,
        setContextChips,
        addContextChip,
        removeContextChip,
        clearContextChips,
      }}
    >
      {children}
    </CopilotContext.Provider>
  );
}

export function useCopilotContext(): CopilotContextValue {
  const context = useContext(CopilotContext);
  if (context === undefined) {
    throw new Error('useCopilotContext must be used within a CopilotProvider');
  }
  return context;
}
